import React from "react";
import ReactDOM from "react-dom/client";
import { createHashHistory, createBrowserHistory } from "@tanstack/react-router";

import "./index.css";

import { isElectron } from "./env";
import { ManagedRelayAuthProvider } from "./cloud/managedAuth";
import { hasCloudPublicConfig } from "./cloud/publicConfig";
import { getRouter } from "./router";
import {
  syncDocumentElectronPlatformClasses,
  syncDocumentWindowControlsOverlayClass,
} from "./lib/windowControlsOverlay";
import { AppRoot } from "./AppRoot";
import { MorrowClerkProvider } from "./components/clerk/MorrowClerkProvider";
import { MorrowClerkAuthProvider } from "./components/clerk/morrowClerkAuth";
import { ensureGuestOwnerId, markWsBootOwner, resolveComposioUserId } from "./composioConnect";
import { installApiFetchAuth } from "./installApiFetchAuth";
import { installRelayClerkTokenPush } from "./relayClerkTokenPush";
import { isClerkReady, publishOwnerIdGetter } from "./clerkTokenBridge";
import { initObservability } from "./observability";

// Observability first: Sentry (errors) + PostHog (events). Inert until keys are
// set, so this is always safe to call and catches boot-time errors too.
initObservability();

// Hosted web authenticates to the cross-origin API with the Clerk token on
// every request. Patch fetch before anything issues a request.
installApiFetchAuth();

// Desktop-only: keep the spawned local backend's relay-token minting fed with a
// current Clerk session token. No-op on web and on bridges lacking the method.
installRelayClerkTokenPush();

// Chat owner id for the API: the Clerk user id when signed in, else a stable
// guest id. Read live so it upgrades from guest to the Clerk user on sign-in.
publishOwnerIdGetter(() => {
  const clerk =
    typeof window !== "undefined"
      ? (window as { Clerk?: { loaded?: boolean; user?: { id?: string } } }).Clerk
      : undefined;
  const clerkUserId = clerk?.user?.id;
  if (clerkUserId) {
    return resolveComposioUserId(clerkUserId);
  }
  // Clerk has finished loading but there is no signed-in user → genuinely
  // signed out (or a lapsed session). Fall back to the guest id even if a
  // stale `user_` owner cookie lingers from a previous sign-in: sending an
  // unauthenticated `user_` owner id makes the backend return
  // `authenticated:false`, and the auth gate then redirects to /pair (a
  // sign-in wall) instead of the guest experience. `ensureGuestOwnerId`
  // returns the guest id and overwrites the stale cookie.
  if (clerk?.loaded === true || isClerkReady()) {
    return ensureGuestOwnerId();
  }
  // Clerk not loaded yet (boot race): keep the persisted id — which may be a
  // `user_` id for a still-signed-in user — so we don't clobber their chat
  // ownership before Clerk resolves.
  return resolveComposioUserId(null);
});

// Chat isolation: ensure this browser's owner-id cookie exists BEFORE the
// connection opens, so the very first WebSocket upgrade and HTTP shell snapshot
// are already scoped to this owner. A persisted Clerk owner cookie is preserved
// (never clobbered by a guest id); a genuinely signed-out browser stamps the
// stable guest id synchronously. We record the boot owner so ChatView can detect
// when a later sign-in makes the live WebSocket stale (still guest) and re-open
// it under the Clerk owner. Same id the app uses for Composio.
markWsBootOwner(resolveComposioUserId(null));

// Electron loads the app from a file-backed shell, so hash history avoids path resolution issues.
const history = isElectron ? createHashHistory() : createBrowserHistory();

const router = getRouter(history);

if (isElectron) {
  syncDocumentElectronPlatformClasses(navigator.platform);
  syncDocumentWindowControlsOverlayClass();
}

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

// Desktop OAuth (Google) redirects back to the app through a custom URL scheme
// (morrowos://app/ in production, morrowos-dev://app/ in development — see
// @clerk/electron's OAuth deep-link transport and apps/desktop
// ElectronProtocol). Clerk's clerk-js refuses to navigate/redirect to any
// protocol outside its built-in allowlist (http:, https:, wails:,
// chrome-extension:), so the desktop forceRedirectUrl (which carries the
// custom scheme, produced by resolveClerkSignInProps for Electron) is rejected
// and sign-in never completes. Extend the allowlist with the desktop schemes so
// Clerk permits the deep-link callback. The renderer is served over the custom
// scheme, so window.location.protocol is the exact scheme for this build; we
// also include every known scheme as a safety net. The pre-rename t3code
// schemes stay listed because the desktop still registers them with the OS, so
// a deep link from an older build can land in this renderer.
const desktopRedirectProtocols = isElectron
  ? Array.from(
      new Set(
        [
          typeof window !== "undefined" ? window.location.protocol : undefined,
          "morrowos:",
          "morrowos-dev:",
          "t3code:",
          "t3code-dev:",
        ].filter((protocol): protocol is string => typeof protocol === "string"),
      ),
    )
  : undefined;

// MorrowOS: Clerk is our primary auth. Mount ClerkProvider whenever a publishable
// key is present, independent of the T3 Connect cloud/relay (which is disabled).
// Only wrap the relay auth provider when that cloud surface is actually configured.
const cloudEnabled = hasCloudPublicConfig();

const app = <AppRoot router={router} />;
const appWithRelay = cloudEnabled ? (
  <ManagedRelayAuthProvider>{app}</ManagedRelayAuthProvider>
) : (
  app
);
const appWithClerkSession = <MorrowClerkAuthProvider>{appWithRelay}</MorrowClerkAuthProvider>;

// MorrowOS front door: the app still renders for signed-out users (sidebar,
// existing threads), but an empty draft shows Clerk's sign-in/sign-up card and
// continues into onboarding after they authenticate. ClerkProvider stays mounted
// so hooks/components keep working; contextual openSignIn() remains for guests
// who hit a signed-in-only action (send after the free message, connect apps).
const gatedApp = appWithClerkSession;

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    {clerkPublishableKey ? (
      <MorrowClerkProvider
        publishableKey={clerkPublishableKey}
        {...(desktopRedirectProtocols
          ? { allowedRedirectProtocols: desktopRedirectProtocols }
          : {})}
      >
        {gatedApp}
      </MorrowClerkProvider>
    ) : (
      appWithClerkSession
    )}
  </React.StrictMode>,
);
